import { css } from '@emotion/css';
import type { ComponentProps } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { hot } from 'react-hot-loader/root';
import { Route, Switch } from 'react-router-dom';

import { FreeTextbooksRedirector } from 'ms-pages/FreeTextbooks/FreeTextbooksRedirector';
import DetailEmptyView from 'ms-pages/Textbooks/components/DetailEmptyView';
import { fontFamily } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import { useGoogleAnalyticsTracking } from 'ms-utils/ga/utils';
import { deserialize } from 'ms-utils/urls/queryParams';

import { FreeTextbooksSubtopicView } from './FreeTextbooksSubtopicView/FreeTextbooksSubtopicView';
import { FreeTextbooksSyllabusSelector } from './FreeTextbooksSyllabusSelector/FreeTextbooksSyllabusSelector';
import FreeTextbooksTopicSidebar from './FreeTextbooksTopicSidebar';
import { FreeTextbooksLayout } from './Layout/Layout';
import {
  freeTextbooksCurriculumSelectorPath,
  freeTextbooksEmptyViewPath,
  freeTextbooksHomePath,
  freeTextbooksRootSelector,
  freeTextbooksSubtopicDetailViewPath,
  freeTextbooksTextbookSelectorPath,
  freeTextbooksTopicEmptyViewPath,
} from './urls';

function FreeTextbooksApp() {
  /* Manually sends Google Analytics pageviews for SPA
  See msproblem/templates/google_analytics/elements/include_google_analytics.html
   */
  useGoogleAnalyticsTracking();

  return (
    <div className={styles.root}>
      <Switch>
        <Route
          path={freeTextbooksHomePath}
          exact
          component={FreeTextbooksRedirector}
        />
        <Route
          path={[
            freeTextbooksRootSelector,
            freeTextbooksCurriculumSelectorPath,
            freeTextbooksTextbookSelectorPath,
          ]}
          exact
          render={({ match: { params } }) => {
            const countryCode: ComponentProps<
              typeof FreeTextbooksSyllabusSelector
              // @ts-expect-error
              // TODO the original implementation was wrong in a number of respects:
              //
              // 1) countryCode does not exist when freeTextbooksRootSelector path matches.
              //    the original code assumed it did.
              //
              // 2) countryCode is not guaranteed to be a CountryCode, its any possible
              //    string. The original code assumed it was a CountryCode
              //
              // FreeTextbooksSyllabusSelector requires being passed a CountryCode
              // so the current implemenation is incorrect. This needs to be fixed.
            >['countryCode'] = params.countryCode;

            return (
              <FreeTextbooksLayout LeftAside={null}>
                <FreeTextbooksSyllabusSelector countryCode={countryCode} />
              </FreeTextbooksLayout>
            );
          }}
        />
        <Route
          render={() => (
            <FreeTextbooksLayout
              LeftAside={() => (
                <Route
                  path={[
                    freeTextbooksEmptyViewPath,
                    freeTextbooksTopicEmptyViewPath,
                    freeTextbooksSubtopicDetailViewPath,
                  ]}
                  exact
                  render={({ match: { params }, location: { search } }) => (
                    <FreeTextbooksTopicSidebar
                      syllabusId={params.syllabusId}
                      activeTopicId={
                        'topicId' in params ? params.topicId : null
                      }
                      activeSubtopicId={
                        'subtopicId' in params ? params.subtopicId : null
                      }
                      searchString={deserialize(search).searchString || ''}
                    />
                  )}
                />
              )}
            >
              <Switch>
                <Route
                  path={freeTextbooksEmptyViewPath}
                  exact
                  render={() => (
                    <DetailEmptyView
                      backgroundColor={colors.white}
                      title="Select a topic to start exploring"
                    />
                  )}
                />
                <Route
                  path={freeTextbooksTopicEmptyViewPath}
                  exact
                  render={() => (
                    <DetailEmptyView
                      backgroundColor={colors.white}
                      title="Select a subtopic to start exploring"
                    />
                  )}
                />
                <Route
                  path={freeTextbooksSubtopicDetailViewPath}
                  exact
                  render={({
                    match: {
                      params: { subtopicId, syllabusId },
                    },
                  }) => (
                    <FreeTextbooksSubtopicView
                      subtopicId={subtopicId}
                      syllabusId={syllabusId}
                    />
                  )}
                />
                <Route
                  render={() => {
                    throw new Error('404');
                  }}
                />
              </Switch>
            </FreeTextbooksLayout>
          )}
        />
      </Switch>
    </div>
  );
}

const styles = {
  root: css({
    fontFamily: fontFamily.body,
  }),
};

export default hot(FreeTextbooksApp);
